import type { Address } from "viem"
import type { Config } from "wagmi"
import { simulateContract } from "@wagmi/core"

import {
  thickV2NfpPositionCollectAbi,
  thickV2NfpPositionDecreaseLiquidityAbi,
} from "@/domains/abis/thick-v2-nfpm-abi"

type NfpmUserStatsProps = {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  liquidity: bigint
  nfpManagerAddress: Address
}

export type NfpmUserStatsReturnType = {
  token0: {
    balance?: bigint
    fees: bigint
  }
  token1: {
    balance?: bigint
    fees: bigint
  }
}

/**
 * Simulate `collect` + `decreaseLiquidity` on the thick-v2 NFPM for a single
 * position to derive the user's current underlying balances and claimable fees
 * without issuing a real transaction.
 *
 * This function intentionally handles one position at a time; batching across
 * many tokenIds is the responsibility of the on-chain transport, which routes
 * repeated calls through `SequentialChunkedRpcStrategy` so metrics roll up.
 */
export const nfpmUserStats = async ({
  account,
  chainId,
  config,
  tokenId,
  liquidity,
  nfpManagerAddress,
}: NfpmUserStatsProps): Promise<NfpmUserStatsReturnType> => {
  const collectParams = {
    tokenId,
    recipient: account,
    amount0Max: BigInt(1e36),
    amount1Max: BigInt(1e36),
  }

  const simulatedCollect = await simulateContract(config, {
    abi: thickV2NfpPositionCollectAbi,
    account,
    chainId,
    functionName: "collect",
    address: nfpManagerAddress,
    args: [collectParams],
  })

  const deadline = Math.floor(Date.now() / 1e3) + 600

  const decreaseParams = {
    tokenId,
    liquidity,
    amount0Min: 0n,
    amount1Min: 0n,
    deadline: BigInt(deadline),
  }

  let decreaseResult: readonly [bigint, bigint] | undefined
  try {
    const simulatedDecrease = await simulateContract(config, {
      abi: thickV2NfpPositionDecreaseLiquidityAbi,
      account,
      chainId,
      functionName: "decreaseLiquidity",
      address: nfpManagerAddress,
      args: [decreaseParams],
    })
    decreaseResult = simulatedDecrease?.result as readonly [bigint, bigint] | undefined
  } catch {
    // Pending fees come from collect. If the optional principal cross-check
    // reverts, keep the fee snapshot instead of failing the whole stats pass.
  }

  const collectResult = simulatedCollect?.result as readonly [bigint, bigint] | undefined

  return {
    token0: {
      balance: decreaseResult?.[0],
      fees: collectResult?.[0] ?? 0n,
    },
    token1: {
      balance: decreaseResult?.[1],
      fees: collectResult?.[1] ?? 0n,
    },
  }
}
